import React from 'react';
import {
  Box,
  FormControl,
  TextField,
  Typography,
  Button,
  CircularProgress,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// Icône de blessure
import HealingIcon from '@mui/icons-material/Healing';

const CreateMedical = ({
  fecha,
  setFecha,
  medicalFuente,
  setMedicalFuente,
  medicalLibelle,
  setMedicalLibelle,
  medicalChirurgie,
  setMedicalChirurgie,
  medicalRecuperation,
  setMedicalRecuperation,
  // Nouveau champ pour afficher le logo de blessure
  medicalAfficheLogoBlessure,
  setMedicalAfficheLogoBlessure,

  markDirty,
  isSubmitting,
  handleCreateCard,
  backendUrl,
  playerType
}) => {
  const { t } = useTranslation();

  // Convertimos a minúsculas para simplificar la comparación:
  const typeLower = (playerType || '').toLowerCase();
  const isPro = typeLower === 'pro';
  const isCantera = typeLower === 'cantera';

  return (
    <Box sx={{ mt: 3 }}>
      {/* Fecha de la ficha medical */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <TextField
          label={t('playerCardCreator.medicalForm.date')}
          variant="outlined"
          type="date"
          value={fecha}
          onChange={(e) => {
            setFecha(e.target.value);
            markDirty();
          }}
          InputLabelProps={{
            shrink: true // Para que el label no se superponga al valor
          }}
        />
      </FormControl>

      {/* Fuente (solo si el jugador es Pro) */}
      {isPro && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <TextField
            label={t('playerCardCreator.medicalForm.source')}
            variant="outlined"
            value={medicalFuente}
            onChange={(e) => {
              setMedicalFuente(e.target.value);
              markDirty();
            }}
            placeholder={t('playerCardCreator.medicalForm.sourcePlaceholder')}
          />
        </FormControl>
      )}

      {/* Lesión (en remplacement de 'Libellé') */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <TextField
          label={t('playerCardCreator.medicalForm.injury')}
          variant="outlined"
          value={medicalLibelle}
          onChange={(e) => {
            setMedicalLibelle(e.target.value);
            markDirty();
          }}
          placeholder={t('playerCardCreator.medicalForm.injuryPlaceholder')}
        />
      </FormControl>

      {/* Cirugía: opciones Sí/No */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <TextField
          select
          label={t('playerCardCreator.medicalForm.surgery')}
          variant="outlined"
          value={medicalChirurgie}
          onChange={(e) => {
            setMedicalChirurgie(e.target.value);
            markDirty();
          }}
        >
          <MenuItem value="">{t('playerCardCreator.medicalForm.select')}</MenuItem>
          <MenuItem value="no">{t('playerCardCreator.medicalForm.no')}</MenuItem>
          <MenuItem value="si">{t('playerCardCreator.medicalForm.yes')}</MenuItem>
        </TextField>
      </FormControl>

      {/* Recuperación */}
      {isPro ? (
        // Si el jugador es Pro, mostramos un select con rangos de tiempo
        <FormControl fullWidth sx={{ mb: 3 }}>
          <TextField
            select
            label={t('playerCardCreator.medicalForm.recovery')}
            variant="outlined"
            value={medicalRecuperation}
            onChange={(e) => {
              setMedicalRecuperation(e.target.value);
              markDirty();
            }}
          >
            <MenuItem value="">{t('playerCardCreator.medicalForm.select')}</MenuItem>
            <MenuItem value="menos de una semana">{t('playerCardCreator.medicalForm.recoveryOptions.lessThanWeek')}</MenuItem>
            <MenuItem value="2 semanas">{t('playerCardCreator.medicalForm.recoveryOptions.twoWeeks')}</MenuItem>
            <MenuItem value="3 semanas">{t('playerCardCreator.medicalForm.recoveryOptions.threeWeeks')}</MenuItem>
            <MenuItem value="4 semanas">{t('playerCardCreator.medicalForm.recoveryOptions.fourWeeks')}</MenuItem>
            <MenuItem value="1-2 meses">{t('playerCardCreator.medicalForm.recoveryOptions.oneToTwoMonths')}</MenuItem>
            <MenuItem value="2-3 meses">{t('playerCardCreator.medicalForm.recoveryOptions.twoToThreeMonths')}</MenuItem>
            <MenuItem value="4-5 meses">{t('playerCardCreator.medicalForm.recoveryOptions.fourToFiveMonths')}</MenuItem>
            <MenuItem value="5-6 meses">{t('playerCardCreator.medicalForm.recoveryOptions.fiveToSixMonths')}</MenuItem>
            <MenuItem value="6 meses - 1 año">{t('playerCardCreator.medicalForm.recoveryOptions.sixMonthsToYear')}</MenuItem>
            <MenuItem value="más de 1 año">{t('playerCardCreator.medicalForm.recoveryOptions.moreThanYear')}</MenuItem>
          </TextField>
        </FormControl>
      ) : (
        // Si el jugador es Cantera, mostramos un input numérico (días)
        isCantera && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <TextField
              label={t('playerCardCreator.medicalForm.recoveryDays')}
              variant="outlined"
              type="number"
              value={medicalRecuperation}
              onChange={(e) => {
                setMedicalRecuperation(e.target.value);
                markDirty();
              }}
              placeholder={t('playerCardCreator.medicalForm.onlyNumbers')}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </FormControl>
        )
      )}

      {/* Slider para 'affiche_logo_blessure' (solo jugadores Pro) */}
      {isPro && (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(medicalAfficheLogoBlessure)}
              onChange={(e) => {
                setMedicalAfficheLogoBlessure(e.target.checked);
                markDirty();
              }}
              color="primary"
            />
          }
          label={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {t('playerCardCreator.medicalForm.showInjuryLogo')} <HealingIcon sx={{ ml: 1 }} />
            </span>
          }
          sx={{ mb: 3 }}
        />
      )}

      {/* Botón de envío */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleCreateCard}
        sx={{ mt: 3 }}
        fullWidth
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            {t('playerCardCreator.medicalForm.saving')}
          </>
        ) : (
          t('playerCardCreator.medicalForm.saveMedical')
        )}
      </Button>
    </Box>
  );
};

export default CreateMedical;
